import jwt from 'jsonwebtoken';
import { Preferences } from '@capacitor/preferences';
import WithBootedClient from '../lib/WithBootedClient';
import IJwt from '../models/IJwt';
import IJwtEntity from '../models/IJwtEntity';
import axios from 'axios';
import { saveProductsToAdjust } from '../pages/tasks/cl-tasks-stock-adjustment/utils';
import md5 from 'md5';
import UserStatusClient from './UserStatusClient';
import { AppVersion, removeFromLocalStore } from '../lib/utils';
import Secrets from '@yumi/secrets';
import SettingsClient from './SettingsClient';
import moment from 'moment-timezone';
import EurekaConsole from '../lib/EurekaConsole';
import { authDiagnostic } from '../lib/auth-diagnostics';

const logger = EurekaConsole({ label: 'authentication-client' });

export async function getAuthFromCache() {
  const cacheAuth = await Preferences.get({ key: userPreferencesKey });
  if (cacheAuth && cacheAuth.value) {
    return JSON.parse(cacheAuth.value!);
  }
  return null;
}

export async function getUserInfoFromCache(): Promise<IJwtEntity | null> {
  const cachedInfo = await Preferences.get({ key: userInfoPreferencesKey });
  if (cachedInfo && cachedInfo.value) {
    return JSON.parse(cachedInfo.value!) as IJwtEntity;
  }
  return null;
}

/* Global vars*/
let decodedJwt: IJwtEntity;
let userInfo: IJwtEntity | undefined;
let authAttempt = 0;
const userPreferencesKey: string = '@user';
const userInfoPreferencesKey: string = '@userInfo';

const apiURL = Secrets.REACT_APP_API_ENDPOINT;
const REMOTE_LOGOUT_TIMEOUT_MS = 15000;

// Legacy consumers read app context before login. This anonymous view carries
// no identity/permissions, never becomes ready and is never written to @userInfo.
const anonymousProfile: IJwtEntity = Object.freeze({
  primarysid: '', unique_name: '', email: '', alias: '', store: '', honorific: '',
  country: 'CL', avatar: '', groupsid: '', iss: '', type: '', aud: '', scope: '',
  iat: 0, exp: 0, businessUnit: 'C004', stores: [], roleName: '',
});

export class ProfileUnavailableError extends Error {
  code = 'PROFILE_UNAVAILABLE';
  status?: number;
  constructor(status?: number) {
    super('No fue posible validar el perfil. Intenta nuevamente.');
    this.name = 'ProfileUnavailableError';
    this.status = Number.isInteger(status) && status !== undefined && status >= 100 && status <= 599 ? status : undefined;
  }
}

const hasMdhIssuer = (claims?: IJwtEntity): boolean => claims?.iss === 'MiLocal MDH Auth';
const isMdhToken = (claims?: IJwtEntity): boolean => {
  const session = (claims as (IJwtEntity & { session?: unknown }) | undefined)?.session;
  return !!claims && hasMdhIssuer(claims) && typeof session === 'string' && session.trim().length > 0 &&
    Number.isInteger(claims.exp) && claims.exp > Math.floor(Date.now() / 1000);
};
interface ServerProfile {
  businessUnit: string; email: string; name: string; primarysid: string;
  scopes: string[]; stores: string[]; store?: string; country: string; roleName?: string;
}
const recordValue = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {};
const isValidProfile = (value: unknown): value is ServerProfile => {
  const data = recordValue(value);
  return typeof data.businessUnit === 'string' && typeof data.email === 'string' && typeof data.name === 'string' &&
    typeof data.primarysid === 'string' && Array.isArray(data.scopes) && data.scopes.every((scope: unknown) => typeof scope === 'string') &&
    Array.isArray(data.stores) && data.stores.every((store: unknown) => typeof store === 'string') &&
    (data.store === undefined || typeof data.store === 'string') && typeof data.country === 'string' &&
    (data.roleName === undefined || typeof data.roleName === 'string');
};
const getStatus = (error: unknown): number | undefined => {
  const status = recordValue(recordValue(error).response).status;
  return typeof status === 'number' && Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined;
};
// mdh-users no longer has the session (401 SESSION_NOT_FOUND; 404 before it answered 401) or the
// MDH token itself is expired/incomplete: only a new login recovers, so Retry would loop forever.
// Network, timeout, 403 and 5xx failures keep the Retry screen.
const LOGIN_REQUIRED_STATUSES = [401, 404];
const requiresLogin = (error: unknown): boolean => {
  const status = error instanceof ProfileUnavailableError ? error.status : getStatus(error);
  return (status !== undefined && LOGIN_REQUIRED_STATUSES.includes(status)) || !isMdhToken(decodedJwt);
};

interface IState {
  isAuthenticated: boolean;
  user?: IJwt;
  provider: string;
}

class AuthenticationClient extends WithBootedClient {
  private profileWrites: Promise<void> = Promise.resolve();

  private isCurrentProfileAttempt(attempt: number, token?: string): boolean {
    return attempt === authAttempt && token === this.state.user?.access_token;
  }

  private mutateProfileCache(attempt: number, token: string | undefined, mutation: () => Promise<void>): Promise<void> {
    const operation = this.profileWrites.then(async () => {
      if (this.isCurrentProfileAttempt(attempt, token)) await mutation();
    });
    this.profileWrites = operation.catch(() => undefined);
    return operation;
  }

  state: IState = {
    isAuthenticated: false,
    provider: '',
  };

  async boot() {
    const attempt = ++authAttempt;
    userInfo = undefined;
    this.state = { isAuthenticated: false, provider: '' };
    let restored: IState | null;
    try {
      restored = await getAuthFromCache();
    } catch {
      return;
    }
    if (attempt !== authAttempt || typeof restored?.user?.access_token !== 'string' || !restored.user.access_token.trim()) return;
    const token = restored.user.access_token;
    this.state = { ...restored, isAuthenticated: false };
    try {
      this.updateJWT(restored.user);
      if (!decodedJwt || typeof decodedJwt.iss !== 'string') throw new ProfileUnavailableError();
      if (!hasMdhIssuer(decodedJwt)) {
        const cached = await getUserInfoFromCache();
        if (attempt !== authAttempt || token !== this.state.user?.access_token) return;
        userInfo = cached ?? undefined;
        this.state = { ...restored, isAuthenticated: Boolean(restored.isAuthenticated && cached) };
        return;
      }
      await Preferences.set({ key: userPreferencesKey, value: JSON.stringify(this.state) });
      if (attempt !== authAttempt || token !== this.state.user?.access_token) return;
      await this.loadUserInfo();
      if (attempt !== authAttempt || token !== this.state.user?.access_token) return;
      this.state = { ...restored, isAuthenticated: true };
      await Preferences.set({ key: userPreferencesKey, value: JSON.stringify(this.state) });
    } catch (error) {
      if (attempt !== authAttempt || token !== this.state.user?.access_token) return;
      if (requiresLogin(error)) {
        const status = error instanceof ProfileUnavailableError ? error.status : getStatus(error);
        logger.info('Authentication diagnostic', { event: 'auth_diagnostic', code: 'boot_login_required', ...(status === undefined ? {} : { status }) });
        await this.signOut();
        return;
      }
      this.state = { ...restored, isAuthenticated: false };
      throw error instanceof ProfileUnavailableError ? error : new ProfileUnavailableError(getStatus(error));
    }
  }

  updateJWT(token: any): void {
    decodedJwt = jwt.decode(token.access_token) as IJwtEntity;
  }

  isAuthenticated(): boolean {
    return this.state.isAuthenticated;
  }

  getInfo(): IJwtEntity {
    return userInfo ?? anonymousProfile;
  }

  async loadUserInfo(): Promise<IJwtEntity> {
    const attempt = authAttempt;
    const token = this.state.user?.access_token;
    try {
      if (!token || !isMdhToken(decodedJwt)) throw new ProfileUnavailableError();
      const options = { headers: { Authorization: `Bearer ${token}`, 'App-Version': AppVersion, ...this.getCloudflareHeaders() } };
      const { data } = await axios.get(`${Secrets.REACT_MDH_USERS_API}/app-bff/login/me`, { ...options, timeout: 15000 });
      if (attempt !== authAttempt || token !== this.state.user?.access_token || !isValidProfile(data)) throw new ProfileUnavailableError();
      const profile: IJwtEntity = { ...decodedJwt, businessUnit: data.businessUnit, email: data.email, unique_name: data.name, primarysid: data.primarysid, scope: data.scopes.join(','), store: data.store ?? '', stores: data.stores, country: data.country, roleName: data.roleName };
      await this.mutateProfileCache(attempt, token, () => Preferences.set({ key: userInfoPreferencesKey, value: JSON.stringify(profile) }));
      if (!this.isCurrentProfileAttempt(attempt, token)) throw new ProfileUnavailableError();
      userInfo = profile;
      return profile;
    } catch (error) {
      if (this.isCurrentProfileAttempt(attempt, token)) {
        userInfo = undefined;
        this.state.isAuthenticated = false;
        try {
          await this.mutateProfileCache(attempt, token, () => Preferences.remove({ key: userInfoPreferencesKey }));
        } catch (cleanupError) {
          logger.warn('Authentication diagnostic', authDiagnostic('load_user_info', cleanupError, 'failed'));
        }
      }
      logger.warn('Authentication diagnostic', authDiagnostic('load_user_info', error, 'failed'));
      throw error instanceof ProfileUnavailableError ? error : new ProfileUnavailableError(getStatus(error));
    }
  }

  setUserInfo(info: Partial<IJwtEntity>): void {
    if (!userInfo || !this.state.isAuthenticated) return;
    userInfo = {
      ...userInfo,
      ...info,
    };
    const profile = userInfo;
    const attempt = authAttempt;
    const token = this.state.user?.access_token;
    void this.mutateProfileCache(attempt, token, () => Preferences.set({
      key: userInfoPreferencesKey,
      value: JSON.stringify(profile),
    })).catch((error) => logger.warn('Authentication diagnostic', authDiagnostic('load_user_info', error, 'failed')));
  }

  async authenticate(provider: string, rawJwt: IJwt) {
    const attempt = ++authAttempt;
    const token = rawJwt?.access_token;
    this.state = { isAuthenticated: false, user: rawJwt, provider };
    userInfo = undefined;
    try {
      if (typeof token !== 'string' || !token.trim()) throw new ProfileUnavailableError();
      this.updateJWT(rawJwt);
      await Preferences.set({ key: userPreferencesKey, value: JSON.stringify(this.state) });
      if (attempt !== authAttempt || token !== this.state.user?.access_token) throw new ProfileUnavailableError();
      SettingsClient.set('TIME_LAST_SEEN', moment(new Date()));
      const profile = await this.loadUserInfo();
      if (attempt !== authAttempt || token !== this.state.user?.access_token) throw new ProfileUnavailableError();
      this.state = { isAuthenticated: true, user: rawJwt, provider };
      await Preferences.set({ key: userPreferencesKey, value: JSON.stringify(this.state) });
      return profile;
    } catch (error) {
      if (attempt === authAttempt && token === this.state.user?.access_token) this.state.isAuthenticated = false;
      throw error instanceof ProfileUnavailableError ? error : new ProfileUnavailableError(getStatus(error));
    }
  }

  /**
   * Check if the scope in token has the roles in rolesToFind
   * @param rolesToFind
   */
  hasRole(rolesToFind: string[] | string): boolean {
    const info = this.getInfo();
    if (!info?.scope) {
      return false;
    }

    const rolesToCheck = Array.isArray(rolesToFind) ? rolesToFind : [rolesToFind];
    for (let index = 0; index < rolesToCheck.length; index++) {
      const roleToCheck = rolesToCheck[index];
      if (info.scope.includes(roleToCheck)) {
        return true;
      }
    }
    return false;
  }

  async signOut() {
    const accessToken = this.state.user?.access_token;
    // Invalidate all in-memory identity before any remote work can suspend the UI.
    this.state = {
      isAuthenticated: false,
      user: undefined,
      provider: '',
    };
    decodedJwt = undefined as unknown as IJwtEntity;
    userInfo = undefined as unknown as IJwtEntity;
    let deadline: ReturnType<typeof setTimeout> | undefined;

    try {
      if (accessToken) {
        await Promise.race([
          axios.delete(`${Secrets.REACT_MDH_USERS_API}/app-bff/logout`, {
            timeout: REMOTE_LOGOUT_TIMEOUT_MS,
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'App-Version': AppVersion,
              ...this.getCloudflareHeaders(),
            },
          }),
          new Promise((_, reject) => {
            deadline = setTimeout(() => reject({ code: 'LOGOUT_TIMEOUT' }), REMOTE_LOGOUT_TIMEOUT_MS);
          }),
        ]);
        logger.info('Authentication logout diagnostic', {
          event: 'auth_logout',
          code: 'remote_revocation_confirmed',
          remoteRevocationConfirmed: true,
        });
      }
    } catch (error: unknown) {
      const source = error && typeof error === 'object' ? error as Record<string, unknown> : {};
      const response = source.response && typeof source.response === 'object' ? source.response as Record<string, unknown> : {};
      const status = typeof response.status === 'number' && Number.isInteger(response.status) && response.status >= 100 && response.status <= 599
        ? response.status
        : undefined;
      logger.warn('Authentication logout diagnostic', {
        event: 'auth_logout',
        code: 'remote_revocation_not_confirmed',
        status,
        remoteRevocationConfirmed: false,
        timeout: Boolean(source.code === 'LOGOUT_TIMEOUT' || source.code === 'ECONNABORTED' || source.code === 'ETIMEDOUT'),
        network: Boolean(!source.response && source.request),
      });
    } finally {
      if (deadline !== undefined) clearTimeout(deadline);
      // A later login owns the shared local keys. An obsolete logout must not
      // erase that new token/profile when its remote request eventually settles.
      if (!this.state.user?.access_token) await this.removeSession();
    }
  }

  async signIn(email: string, Password: string) {
    const password = md5(Password);
    const appVersion = await UserStatusClient.getAppData();

    const options = {
      headers: {
        'App-Version': AppVersion,
        ...this.getCloudflareHeaders(),
      },
    };
    return await axios.post(`${apiURL}/oauth/login`, { email, password, ...appVersion }, options);
  }

  async authorizeAdfsToken(tokenData: IJwt) {
    const appVersion = await UserStatusClient.getAppData();
    const options = {
      headers: {
        Authorization: `${tokenData.token_type} ${tokenData.access_token}`,
        'App-Version': AppVersion,
        ...this.getCloudflareHeaders(),
      },
    };
    return await axios.post(`${apiURL}/oauth/login/token`, appVersion, options);
  }

  async signInFromIAMToken(tokenData: IJwt, deviceToken: string) {
    const appVersion = await UserStatusClient.getAppData();
    const options = {
      headers: {
        Authorization: `${tokenData.token_type} ${tokenData.access_token}`,
        'App-Version': AppVersion,
        ...this.getCloudflareHeaders(),
      },
    };
    return await axios.post(`${Secrets.REACT_MDH_USERS_API}/app-bff/login`, { ...appVersion, device_token: deviceToken }, options);
  }

  private async removeSession() {
    this.state = {
      isAuthenticated: false,
      user: undefined,
      provider: '',
    };
    userInfo = undefined as unknown as IJwtEntity;
    decodedJwt = undefined as unknown as IJwtEntity;

    const cleanup = [
      Promise.resolve().then(() => Preferences.remove({ key: 'SECURITY_PIN' })),
      Promise.resolve().then(() => Preferences.remove({ key: userPreferencesKey })),
      Promise.resolve().then(() => Preferences.remove({ key: userInfoPreferencesKey })),
      Promise.resolve().then(() => saveProductsToAdjust([])),
      Promise.resolve().then(() => removeFromLocalStore('SAP_USER')),
    ];
    const results = await Promise.allSettled(cleanup);
    if (results.some((result) => result.status === 'rejected')) {
      const error = new Error('Local logout cleanup failed');
      error.name = 'LocalLogoutCleanupError';
      throw error;
    }
  }

  async setState(newState: IState): Promise<void> {
    this.state = newState;
  }

  private getCloudflareHeaders() {
    return Secrets.REACT_APP_ENVIRONMENT === 'STAGING'
      ? {
          'CF-Access-Client-Id': Secrets.CF_ACCESS_CLIENT_ID,
          'CF-Access-Client-Secret': Secrets.CF_ACCESS_CLIENT_SECRET,
        }
      : {};
  }
}

export default new AuthenticationClient();
